import { Box, Checkbox } from '@chakra-ui/react';
import {
	chakraComponents,
	type DropdownIndicatorProps,
	type GroupBase,
	type MenuListProps,
	type OptionBase,
	type OptionProps,
	type PlaceholderProps,
	type ControlProps,
} from 'chakra-react-select';
import type React from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { FaRobot } from 'react-icons/fa';
import { FiChevronDown } from 'react-icons/fi';
import { MdError } from 'react-icons/md';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import { colors } from '@/components/ui/theme';
import type { CustomCheckboxLabelProps } from './FaceliftSelect.types';

const CustomCheckbox = {
	Label: (props: CustomCheckboxLabelProps) => <Checkbox.Label {...props} />,
};

export const CheckboxOption = <
	Option extends OptionBase,
	IsMulti extends boolean,
>(
	props: OptionProps<Option, IsMulti, GroupBase<Option>>,
) => {
	const handleMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
	};

	// Check if this is the "Select All" option
	const isSelectAll = (props.data as any)?.__isSelectAll === true;
	const allSelected = (props.data as any)?.__allSelected === true;
	
	// Handle Select All click
	const handleSelectAllClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		
		const onChange = props.selectProps.onChange as
			| ((value: unknown, actionMeta?: unknown) => void)
			| undefined;
		if (!onChange) return;
		
		const allOptions = (props.selectProps.options || []) as Option[];
		// Filter out the Select All option itself
		const realOptions = allOptions.filter(
			(opt) => (opt as any)?.__isSelectAll !== true
		);
		
		if (allSelected) {
			// Deselect all
			onChange([], { action: 'deselect-option' });
		} else {
			// Select all available options
			onChange(realOptions, { action: 'select-option' });
			props.selectProps.onMenuClose();
		}
	};

	return (
		<chakraComponents.Option
			{...props}
			innerProps={{
				...props.innerProps,
				onClick: isSelectAll
					? handleSelectAllClick
					: props.innerProps?.onClick,
			}}
		>
			<Show when={props.selectProps.isMulti}>
				<Checkbox.Root
					size="xs"
					variant="solid"
					checked={isSelectAll ? allSelected : props.isSelected}
					onClick={isSelectAll ? handleSelectAllClick : handleMouseDown}
					_checked={{
						'& .chakra-checkbox__control': {
							background: 'brand.solid',
							borderColor: 'brand.solid',
						},
					}}
				>
					<Checkbox.HiddenInput />
					<Checkbox.Control />
					<CustomCheckbox.Label>
						<FaceliftText fontSize="sm" fontWeight="normal">
							{props.label}
						</FaceliftText>
					</CustomCheckbox.Label>
				</Checkbox.Root>
			</Show>
			<Show when={!props.selectProps.isMulti}>
				<FaceliftText fontSize="xs">{props.label}</FaceliftText>
			</Show>
		</chakraComponents.Option>
	);
};

export const DropdownIndicatorLocal = <
	Option extends OptionBase,
	IsMulti extends boolean,
>(
	props: DropdownIndicatorProps<Option, IsMulti, GroupBase<Option>> & {
		ai?: boolean;
		error?: string;
		color?: string;
	},
) => {
	let Icon: React.ElementType = FiChevronDown;
	let iconColor = props.color || 'black';

	if (props.ai) {
		Icon = FaRobot;
		iconColor = colors.robotGrey;
	} else if (props.error) {
		Icon = MdError;
		iconColor = colors.errors;
	} else {
		Icon = FiChevronDown;
	}

	return (
		<chakraComponents.DropdownIndicator {...props}>
			<Icon size={12} color={iconColor} />
		</chakraComponents.DropdownIndicator>
	);
};

export const MenuListLocal = <
	Option extends OptionBase,
	IsMulti extends boolean,
>(
	props: MenuListProps<Option, IsMulti, GroupBase<Option>>,
) => {
	const { children, ...restProps } = props;
	return (
		<chakraComponents.MenuList {...restProps}>
			{children}
		</chakraComponents.MenuList>
	);
};

export const CustomPlaceholderLocal = <
	Option extends OptionBase,
	IsMulti extends boolean,
>(
	props: PlaceholderProps<Option, IsMulti, GroupBase<Option>>,
) => {
	const isMultiSelect = props.selectProps.isMulti || props.selectProps.isSearchable;
	return (
		<chakraComponents.Placeholder {...props}>
			{isMultiSelect && <AiOutlineSearch size={16} />}
			{props.children}
		</chakraComponents.Placeholder>
	);
};
